<template>
  <section class="open-slas-view">
    <AdminHeader
      :texts="{ title: $t('site_alerts') }"
      :isSearchShown="false"
      :isAddShown="false"
    >
      <TableSearch
        :tableTitles="tableTitles"
        @input="handleInput"
      />
    </AdminHeader>
    <Loading v-if="isLoading" />
    <template v-if="!isLoading && sitesToShow.length">
      <div class="site-alerts-table">
        <div class="table-header">
          <p class="col-no">#</p>
          <p
            class="col-company sortable"
            :class="{ active: sortBy === 'company_name', [sortDir]: sortBy === 'company_name' }"
            @click="handleSort('company_name')"
          >
            {{ $t('company_name') }}
            <span v-if="sortBy === 'company_name'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </p>
          <p
            class="col-site sortable"
            :class="{ active: sortBy === 'name', [sortDir]: sortBy === 'name' }"
            @click="handleSort('name')"
          >
            {{ $t('site') }}
            <span v-if="sortBy === 'name'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </p>
          <p
            class="col-total-rooms sortable"
            :class="{ active: sortBy === 'total_rooms', [sortDir]: sortBy === 'total_rooms' }"
            @click="handleSort('total_rooms')"
          >
            {{ $t('site_alerts_total_rooms') }}
            <span v-if="sortBy === 'total_rooms'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </p>
          <p
            class="col-no-cleaner sortable"
            :class="{ active: sortBy === 'rooms_without_cleaner_arrival', [sortDir]: sortBy === 'rooms_without_cleaner_arrival' }"
            @click="handleSort('rooms_without_cleaner_arrival')"
          >
            {{ $t('rooms_without_cleaner_arrival_percentage') }}
            <span v-if="sortBy === 'rooms_without_cleaner_arrival'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </p>
          <p
            class="col-open-complaints sortable"
            :class="{ active: sortBy === 'rooms_with_open_complaints', [sortDir]: sortBy === 'rooms_with_open_complaints' }"
            @click="handleSort('rooms_with_open_complaints')"
          >
            {{ $t('site_alerts_open_complaints_count') }}
            <span v-if="sortBy === 'rooms_with_open_complaints'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </p>
          <p
            class="col-opening-date sortable"
            :class="{ active: sortBy === 'official_start_date', [sortDir]: sortBy === 'official_start_date' }"
            @click="handleSort('official_start_date')"
          >
            {{ $t('official_start_date') }}
            <span v-if="sortBy === 'official_start_date'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </p>
          <p
            class="col-country sortable"
            :class="{ active: sortBy === 'company_country', [sortDir]: sortBy === 'company_country' }"
            @click="handleSort('company_country')"
          >
            {{ $t('company_country') }}
            <span v-if="sortBy === 'company_country'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
          </p>
          <p class="col-whatsapp"></p>
        </div>
        <div
          v-for="(site, idx) in paginatedSites"
          :key="site.id"
          class="table-row"
        >
          <p class="col-no">{{ paginationStart + idx }}</p>
          <p class="col-company table-cell text-cell">{{ site.company_name }}</p>
          <p class="col-site table-cell text-cell">{{ site.name }}</p>
          <p class="col-total-rooms">{{ site.total_rooms }}</p>
          <p class="col-no-cleaner">{{ site.rooms_without_cleaner_arrival }}</p>
          <p class="col-open-complaints">{{ site.rooms_with_open_complaints }}</p>
          <p class="col-opening-date">{{ formatOpeningDate(site.official_start_date) }}</p>
          <p class="col-country table-cell text-cell">{{ site.company_country || '—' }}</p>
          <p class="col-whatsapp">
            <button
              type="button"
              class="whatsapp-button"
              :title="$t('send_whatsapp')"
              @click.stop="openWhatsApp(site)"
            >
              <svg
                class="whatsapp-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                />
              </svg>
              <span>{{ $t('whatsapp') }}</span>
            </button>
          </p>
        </div>
      </div>
      <div class="pagination-container" v-if="sortedSites.length > 0 && totalPages > 1">
        <div class="pagination-info">
          {{ $t('showing') }} {{ paginationStart }}-{{ paginationEnd }} {{ $t('of') }} {{ sortedSites.length }}
        </div>
        <div class="pagination-controls">
          <label class="per-page-label">
            {{ $t('per_page') || 'Per page' }}:
            <select v-model="itemsPerPage" class="per-page-select" @change="handlePerPageChange">
              <option v-for="opt in itemsPerPageOptions" :key="opt" :value="opt">{{ opt }}</option>
            </select>
          </label>
          <button
            type="button"
            class="pagination-btn"
            :disabled="currentPage === 1"
            @click="setPage(currentPage - 1)"
          >
            {{ $t('previous') }}
          </button>
          <div class="pagination-pages">
            <button
              v-for="page in visiblePages"
              :key="page"
              type="button"
              class="pagination-page"
              :class="{ active: currentPage === page }"
              @click="setPage(page)"
            >
              {{ page }}
            </button>
          </div>
          <button
            type="button"
            class="pagination-btn"
            :disabled="currentPage === totalPages"
            @click="setPage(currentPage + 1)"
          >
            {{ $t('next') }}
          </button>
        </div>
      </div>
    </template>
    <div v-if="!isLoading && !sitesToShow.length && sitesWithSLAs.length" class="no-results">
      <p>{{ $t('no_results_found') || 'No results found' }}</p>
    </div>
    <div v-if="!isLoading && !sitesWithSLAs.length" class="no-slas">
      <h3>{{ $t('no_open_slas') }}</h3>
      <p>{{ $t('no_open_slas_description') || 'There are currently no sites with open SLAs.' }}</p>
    </div>

    <MessageSelectionModal
      :show="showMessageModal"
      :site="selectedSite"
      @close="closeMessageModal"
      @select="handleMessageSelect"
    />
  </section>
</template>

<script>
import AdminHeader from '../components/AdminHeader'
import Loading from '@/modules/common/components/Loading'
import TableSearch from '@/modules/common/components/customTable/TableSearch'
import slaService from '../services/slaService'
import util from '@/modules/common/services/utilService'
import { Toast } from '@/modules/common/Toast'
import { openWhatsApp } from '../utils/whatsAppUtils'
import MessageSelectionModal from '../components/MessageSelectionModal'

export default {
  name: 'OpenSLAs',
  components: {
    AdminHeader,
    Loading,
    TableSearch,
    MessageSelectionModal,
  },
  data() {
    return {
      sitesWithSLAs: [],
      isLoading: false,
      searchTxt: '',
      searchBy: 'name',
      tableTitles: ['name', 'company_name'],
      showMessageModal: false,
      selectedSite: null,
      sortBy: null,
      sortDir: 'asc',
      currentPage: 1,
      itemsPerPage: 10,
      itemsPerPageOptions: [10, 25, 50, 100],
    }
  },
  watch: {
    searchTxt() {
      this.currentPage = 1
    },
    sortBy() {
      this.currentPage = 1
    },
    sortDir() {
      this.currentPage = 1
    },
    totalPages(newTotal) {
      if (this.currentPage > newTotal && newTotal > 0) {
        this.currentPage = newTotal
      }
    },
  },
  computed: {
    sitesToShow() {
      if (!this.searchTxt) return this.sitesWithSLAs
      return util.filterArrayOfObjectsWithString(
        this.sitesWithSLAs,
        this.searchTxt,
        this.searchBy
      )
    },
    sortedSites() {
      const list = [...this.sitesToShow]
      if (!this.sortBy) return list
      const dir = this.sortDir === 'asc' ? 1 : -1
      list.sort((a, b) => {
        const va = this.getSortValue(a, this.sortBy)
        const vb = this.getSortValue(b, this.sortBy)
        if (va === vb) return 0
        if (va == null || va === '') return 1 * dir
        if (vb == null || vb === '') return -1 * dir
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        if (typeof va === 'object' && va instanceof Date && typeof vb === 'object' && vb instanceof Date) {
          return (va.getTime() - vb.getTime()) * dir
        }
        return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir
      })
      return list
    },
    totalPages() {
      if (this.sortedSites.length === 0) return 0
      return Math.ceil(this.sortedSites.length / this.itemsPerPage)
    },
    paginationStart() {
      return (this.currentPage - 1) * this.itemsPerPage + 1
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.sortedSites.length)
    },
    paginatedSites() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      return this.sortedSites.slice(start, start + this.itemsPerPage)
    },
    visiblePages() {
      const maxVisible = 5
      if (this.totalPages <= maxVisible) {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1)
      }
      const start = Math.max(this.currentPage - Math.floor(maxVisible / 2), 1)
      const end = Math.min(start + maxVisible - 1, this.totalPages)
      const adjStart = Math.max(1, end - maxVisible + 1)
      return Array.from({ length: Math.min(maxVisible, this.totalPages) }, (_, i) => adjStart + i)
    },
  },
  async created() {
    await this.loadSitesWithOpenSLAs()
  },
  methods: {
    async loadSitesWithOpenSLAs() {
      this.isLoading = true
      try {
        const sites = await slaService.getSitesWithOpenSLAs()
        this.sitesWithSLAs = sites
      } catch (error) {
        console.error('Error loading sites with open SLAs:', error)
      } finally {
        this.isLoading = false
      }
    },
    formatOpeningDate(value) {
      if (value == null || value === '') return '—'
      const d = new Date(value)
      return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB')
    },
    openWhatsApp(site) {
      this.selectedSite = site
      this.showMessageModal = true
    },
    closeMessageModal() {
      this.showMessageModal = false
      this.selectedSite = null
    },
    handleMessageSelect(messageType) {
      if (!this.selectedSite) return

      let message = ''
      let roomNames = []

      if (messageType === 'no_cleaner_arrival') {
        message = this.$t('message_no_cleaner_arrival')
        roomNames = this.selectedSite.rooms_without_cleaner_arrival_names || []
      } else if (messageType === 'open_complaints') {
        message = this.$t('message_open_complaints')
        roomNames = this.selectedSite.rooms_with_open_complaints_names || []
      }

      if (roomNames && roomNames.length > 0) {
        const roomsList = roomNames.map((name, index) => `${index + 1}. ${name}`).join('\n')
        message += `\n\n${this.$t('rooms_list') || 'Rooms:'}\n${roomsList}`
      }

      if (message) {
        try {
          openWhatsApp(message)
          new Toast({
            message: this.$t('opening_whatsapp') || 'Opening WhatsApp...',
            type: 'success',
            duration: 2000
          })
          this.closeMessageModal()
        } catch (error) {
          console.error('Error opening WhatsApp:', error)
          new Toast({
            message: this.$t('error_opening_whatsapp') || 'Error opening WhatsApp. Please try again.',
            type: 'danger',
            duration: 4000
          })
        }
      }
    },
    handleInput(value, searchBy) {
      if (searchBy) this.searchBy = searchBy
      this.searchTxt = value
    },
    handleSort(field) {
      if (this.sortBy === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = field
        this.sortDir = 'asc'
      }
    },
    getSortValue(site, field) {
      const v = site[field]
      if (field === 'official_start_date') {
        if (v == null || v === '') return null
        const d = new Date(v)
        return isNaN(d.getTime()) ? null : d
      }
      if (field === 'total_rooms' || field === 'rooms_without_cleaner_arrival' || field === 'rooms_with_open_complaints') {
        return typeof v === 'number' ? v : (v != null && v !== '' ? Number(v) : null)
      }
      return v != null ? String(v).trim() : ''
    },
    setPage(page) {
      const p = Math.max(1, Math.min(page, this.totalPages))
      if (p !== this.currentPage) this.currentPage = p
    },
    handlePerPageChange() {
      this.currentPage = 1
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/styles/vars.scss' as *;
@use '@/styles/mixins.scss' as *;

.open-slas-view {
  padding: 20px;
  min-height: calc(100vh - 200px);
}

.site-alerts-table {
  margin-top: 20px;
  border: 1px solid #e7e7e7;
  border-radius: 4px;
  overflow: hidden;
  background: $white;
}

.table-header {
  display: grid;
  grid-template-columns: 50px 180px 180px 90px 90px 100px 120px 130px 120px;
  width: 100%;
  border-bottom: 4px solid #e7e7e7;
  min-height: 6vh;
  background: #fafafa;
  font-weight: $font-weight-semi-bold;
  color: #606060;

  p {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    margin: 0;
    font-size: $font-size-regular;
  }

  .sortable {
    cursor: pointer;
    user-select: none;
    gap: 4px;

    &:hover {
      color: #333;
    }

    &.active {
      color: #1976d2;
    }
  }

  .sort-arrow {
    font-size: 0.7em;
    opacity: 0.9;
  }
}

.table-row {
  display: grid;
  grid-template-columns: 50px 180px 180px 90px 90px 100px 120px 130px 120px;
  width: 100%;
  border-bottom: 1px solid #e7e7e7;
  min-height: 5vh;
  align-items: center;

  &:hover {
    background-color: #f5fafe;
  }

  p {
    margin: 0;
    padding: 12px 16px;
    font-size: $font-size-regular;
    color: #333;
  }

  .table-cell {
    overflow: hidden;

    &.text-cell > span,
    &.text-cell {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }
}

.col-no {
  text-align: center;
  font-weight: $font-weight-regular;
}

.col-total-rooms,
.col-no-cleaner,
.col-open-complaints {
  text-align: center;
}

.col-whatsapp {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px !important;
}

.whatsapp-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #25d366;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 0.9rem;
  font-weight: $font-weight-semi-bold;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(37, 211, 102, 0.3);
}

.whatsapp-button:hover {
  background-color: #20ba5a;
  box-shadow: 0 4px 8px rgba(37, 211, 102, 0.4);
  transform: translateY(-1px);
}

.whatsapp-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.pagination-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #e7e7e7;
  border-radius: 4px;
  font-size: $font-size-regular;
}

.pagination-info {
  color: #606060;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.per-page-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-weight: $font-weight-regular;
  color: #606060;

  .per-page-select {
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: $white;
    font-size: $font-size-regular;
    cursor: pointer;
  }
}

.pagination-btn {
  padding: 8px 14px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: $white;
  font-size: $font-size-regular;
  cursor: pointer;
  color: #333;

  &:hover:not(:disabled) {
    background: #f0f0f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.pagination-pages {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pagination-page {
  min-width: 36px;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: $white;
  font-size: $font-size-regular;
  cursor: pointer;
  color: #333;

  &:hover {
    background: #f0f0f0;
  }

  &.active {
    background: #1976d2;
    border-color: #1976d2;
    color: white;
  }
}

.no-slas,
.no-results {
  text-align: center;
  padding: 60px 20px;
  color: $dark-grey;

  h3 {
    font-size: 1.5rem;
    margin: 0 0 12px 0;
    color: $black-blue;
    font-weight: $font-weight-bold;
  }

  p {
    font-size: 1rem;
    color: $dark-grey;
    margin: 0;
  }
}

.no-results {
  padding: 40px 20px;

  p {
    font-size: 1.1rem;
    color: $dark-grey;
  }
}

@media (max-width: 1200px) {
  .table-header,
  .table-row {
    grid-template-columns: 40px 140px 140px 70px 70px 80px 100px 110px 100px;
    font-size: 0.9rem;
  }
}

@media (max-width: 768px) {
  .open-slas-view {
    padding: 15px;
  }

  .site-alerts-table {
    overflow-x: auto;
  }

  .table-header,
  .table-row {
    grid-template-columns: 35px 120px 120px 60px 60px 70px 90px 100px 90px;
    min-width: 745px;
    font-size: 0.85rem;
  }
}
</style>
